import React, { useState } from 'react';
import { TrendingUp, Clock, Target, BookOpen, Calculator, Microscope } from 'lucide-react';
import { useChildren } from '../hooks/useChildren';
import { useProgress } from '../hooks/useProgress';
import { ChildCard } from '../components/ChildCard';

const subjectIcons = {
  math: Calculator,
  reading: BookOpen,
  science: Microscope,
};

const subjectColors = {
  math: 'blue',
  reading: 'green',
  science: 'purple',
};

export function ProgressPage() {
  const { children } = useChildren();
  const [selectedChild, setSelectedChild] = useState<string | null>(null);
  const { progress, loading, getSubjectProgress, getAverageScore, getTotalTimeSpent } = useProgress(selectedChild);

  const selectedChildData = children.find(child => child.id === selectedChild);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getSubjectStats = () => {
    const subjects = ['math', 'reading', 'science'];
    return subjects.map(subject => {
      const subjectProgress = getSubjectProgress(subject);
      const avgScore = subjectProgress.length > 0 
        ? Math.round(subjectProgress.reduce((sum, p) => sum + p.score, 0) / subjectProgress.length)
        : 0;
      const totalTime = subjectProgress.reduce((sum, p) => sum + p.time_spent, 0);
      
      return {
        subject,
        completed: subjectProgress.length,
        avgScore,
        totalTime,
      };
    });
  };

  if (children.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <TrendingUp className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No Progress Data</h2>
        <p className="text-gray-600">Add a child and complete some activities to see progress.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Child Selection */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Learning Progress</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {children.map((child) => (
            <ChildCard
              key={child.id}
              child={child}
              onClick={() => setSelectedChild(child.id)}
              isSelected={selectedChild === child.id}
            />
          ))}
        </div>
      </div>

      {/* Progress Details */}
      {selectedChild && selectedChildData && (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {selectedChildData.name}'s Learning Journey
            </h3>
            <p className="text-gray-600">Track progress across all subjects and activities</p>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading progress data...</p>
            </div>
          ) : (
            <>
              {/* Overall Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Target className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{progress.length}</div>
                  <div className="text-sm text-gray-600">Activities Completed</div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{getAverageScore()}%</div>
                  <div className="text-sm text-gray-600">Average Score</div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Clock className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{formatTime(getTotalTimeSpent())}</div>
                  <div className="text-sm text-gray-600">Time Spent Learning</div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200 text-center">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Target className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{selectedChildData.current_level}</div>
                  <div className="text-sm text-gray-600">Current Level</div>
                </div>
              </div>

              {/* Subject Breakdown */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Progress by Subject</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {getSubjectStats().map((stat) => {
                    const IconComponent = subjectIcons[stat.subject as keyof typeof subjectIcons];
                    const colorClass = subjectColors[stat.subject as keyof typeof subjectColors];
                    
                    return (
                      <div key={stat.subject} className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className={`p-3 rounded-lg bg-${colorClass}-100`}>
                            <IconComponent className={`w-6 h-6 text-${colorClass}-600`} />
                          </div>
                          <div>
                            <h5 className="font-semibold text-gray-900 capitalize">{stat.subject}</h5>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Activities Completed</span>
                            <span className="font-semibold text-gray-900">{stat.completed}</span>
                          </div>
                          
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Average Score</span>
                            <span className="font-semibold text-gray-900">{stat.avgScore}%</span>
                          </div>
                          
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Time Spent</span>
                            <span className="font-semibold text-gray-900">{formatTime(stat.totalTime)}</span>
                          </div>

                          {stat.avgScore > 0 && (
                            <div className="mt-3">
                              <div className="bg-gray-200 rounded-full h-2">
                                <div
                                  className={`bg-${colorClass}-500 h-2 rounded-full transition-all duration-300`}
                                  style={{ width: `${stat.avgScore}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recent Activities */}
              {progress.length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h4>
                  <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                    <div className="max-h-96 overflow-y-auto">
                      {progress.slice(0, 10).map((item, index) => (
                        <div key={item.id} className={`p-4 ${index !== progress.length - 1 ? 'border-b border-gray-100' : ''}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className={`p-2 rounded-lg bg-${subjectColors[item.activities?.subject as keyof typeof subjectColors]}-100`}>
                                {React.createElement(subjectIcons[item.activities?.subject as keyof typeof subjectIcons], {
                                  className: `w-4 h-4 text-${subjectColors[item.activities?.subject as keyof typeof subjectColors]}-600`
                                })}
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">{item.activities?.title}</div>
                                <div className="text-sm text-gray-600 capitalize">{item.activities?.subject}</div>
                              </div>
                            </div>
                            
                            <div className="text-right">
                              <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                item.score >= 90 ? 'bg-green-100 text-green-800' :
                                item.score >= 70 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {item.score}%
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {new Date(item.completed_at).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}